import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import { pdf } from "@react-pdf/renderer";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { storage } from "../firebaseconfig"; 
import { KeyUri } from "../key";
import {
  Check,
  Copy,
  MapPin,
  Calendar,
  Users,
  ShieldCheck,
  Download,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  Phone,

} from "lucide-react";
import TourInvoicePDF from "./TourInvoicePDF";

function PhoneAction({ phone, label = 'Contact', size = 'small' }) {
  const [copied, setCopied] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const isMobile = typeof window !== 'undefined' ? window.innerWidth <= 768 : false;

  const handleCopy = async () => {
    if (!phone) {
      alert('Phone number not available');
      return;
    }
    
    try {
      await navigator.clipboard.writeText(phone);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      
    } catch (err) {
      console.warn("Clipboard failed:", err);
      const textArea = document.createElement("textarea");
      textArea.value = phone;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      
    }
  };

  const handleDial = () => {
    window.open(`tel:${phone.replace(/[^0-9+]/g, '')}`, '_blank');
  };

  const handleClick = () => {
    if (isMobile) {
      handleDial();
    } else {
      setShowCopyModal(true);
    }
  };

  const closeModal = () => {
    setShowCopyModal(false);
    setCopied(false);
  };

  if (!phone) return null;

  return (
    <>
      <button
        onClick={handleClick}
        className={`w-full flex items-center justify-center gap-3 p-3 rounded-xl border-2 font-semibold transition-all ${
          isMobile
            ? 'bg-indigo-600 hover:bg-indigo-700 text-white border-indigo-600 shadow-lg hover:shadow-xl hover:-translate-y-0.5'
            : 'bg-white hover:bg-indigo-50 border-indigo-300 hover:border-indigo-500 text-indigo-700 shadow-sm hover:shadow-md'
        } ${size === 'medium' ? 'text-base py-3.5' : 'text-sm py-2.5'}`}
      >
        <Phone size={20} className={isMobile ? 'text-white' : 'text-indigo-600'} />
        {label}
        {!isMobile && <Copy size={18} className="text-indigo-400 ml-auto" />}
      </button>

      {/* ✅ FIXED: Div Modal instead of <dialog> */}
      {showCopyModal && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/20 z-50 backdrop-blur-sm" 
            onClick={closeModal}
          />
          
          {/* Modal */}
          <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50">
            <div className="bg-white p-6 rounded-2xl shadow-2xl max-w-sm w-full max-h-[90vh] overflow-y-auto border border-gray-200">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Copy size={28} className="text-indigo-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Copy Phone Number</h3>
                <p className="text-gray-600 mb-6">Tap to copy operator number</p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-xl border-2 border-dashed border-gray-300 mb-6 text-center">
                <div className="font-mono text-2xl font-bold text-gray-900 tracking-wide break-all">
                  {phone}
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={handleCopy}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-4 rounded-xl transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 flex items-center justify-center gap-2 text-sm"
                >
                  {copied ? (
                    <>
                      <Check size={20} className="text-green-400" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy size={20} />
                      Copy Number
                    </>
                  )}
                </button>
                <button
                  onClick={closeModal}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-4 rounded-xl transition-all text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}


export default function TourBookingInvoice() {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const bookingIdFromState = location.state?.bookingId;
  const bookingId = searchParams.get("bookingId") || bookingIdFromState;

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(true);
  const [copied, setCopied] = useState(false);
  const [pricing, setPricing] = useState(null);
  
  const [invoiceSynced, setInvoiceSynced] = useState(false);
  const [paymentFailed, setPaymentFailed] = useState(false);

  useEffect(() => {
    if (!bookingId) {
      setLoading(false);
      return;
    }

    const orderId = searchParams.get("order_id"); 

        const isBalanceOrder = orderId?.charAt(4) === "B";

    const fetchBookingData = async () => {
      try {
        // Returning from HDFC: confirm payment before showing the booking
        if (orderId) {
          try {
            const verifyRes = await axios.post(
              `${KeyUri.BACKEND_URI}/payments/hdfc/verify`,
              { orderId },
              { headers: { "Content-Type": "application/json" } }
            );
            if (!verifyRes.data.success) {
              setPaymentFailed(true);
              setBooking(null);
              setLoading(false);
              return;
            }

                        const savedCoupon = sessionStorage.getItem("hdfc_tour_coupon");
            if (savedCoupon && !isBalanceOrder) {
              sessionStorage.removeItem("hdfc_tour_coupon");
              try {
                const token = localStorage.getItem("tokenUser")?.replace(/^"|"$/g, "");
                await axios.post(
                  `${KeyUri.BACKEND_URI}/tour-coupon/apply`,
                  { ...JSON.parse(savedCoupon), bookingRef: bookingId },
                  { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
                );
              } catch (e) {
                console.error("Coupon apply error:", e);
              }
            }

          } catch (verifyErr) {
            console.error("HDFC verify failed:", verifyErr);
            setPaymentFailed(true);
            setBooking(null);
            setLoading(false);
            return;
          }
        }

        const res = await axios.get(
          `${KeyUri.BACKEND_URI}/get-tour-booking/${bookingId}`
        );
        setBooking(res.data.data || res.data);
      } catch (err) {
        console.error("Error fetching tour booking:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchBookingData();
  }, [bookingId, searchParams]);

  useEffect(() => {
  const fetchPricing = async () => {
    try {
      const res = await axios.get(`${KeyUri.BACKEND_URI}/tourpricing`);
      setPricing(res.data);
    } catch (err) {
      console.error("Failed to fetch tour pricing:", err);
    }
  };
  fetchPricing();
}, []);
  // Add this useEffect after fetchBookingData
useEffect(() => {
  if (booking) {

  }
}, [booking]);

useEffect(() => {
  
  if (booking && pricing) {
  
  }
}, [booking, pricing]);

  
  useEffect(() => {
    if (!booking || invoiceSynced) return;

        if (["pending", "failed"].includes(booking.status)) return;

    const generateAndUpload = async () => {
      try {
        setInvoiceSynced(true);
       
const isBalanceReturn = searchParams.get("order_id")?.charAt(4) === "B";
        // 1) Generate PDF blob
       const blob = await pdf(<TourInvoicePDF invoiceData={{ ...booking, pricing }} />).toBlob();
      

        // 2) Upload to Firebase (TOUR folder)
        const storageRef = ref(storage, `tour-pdfs/tour_${booking._id}.pdf`);
      
        await uploadBytes(storageRef, blob);
       

        // 3) Get URL (for debug/backend)
        const downloadURL = await getDownloadURL(storageRef);
        

        // 4) Trigger WhatsApp confirmation (HOTEL PATTERN)
      if (!isBalanceReturn) {
  try {
    await axios.post(`${KeyUri.BACKEND_URI}/tour-confirm`, {
      booking_idd: booking._id,
      invoice_url: downloadURL,
    });
  } catch (confirmErr) {
    console.error(" TOUR confirmation failed (non-blocking):", confirmErr);
  }
}
      } catch (err) {
        console.error(" Auto TOUR PDF FAILED:", err);
        // Silent fail - don't show toast on page load
      }
    };

    generateAndUpload();
  }, [booking, invoiceSynced]);

  const handleDownloadPDF = async () => {
    if (loading || !booking) {
      alert("Loading data. Please wait.");
      return;
    }

    try {
      setIsGeneratingPdf(true);

      // Generate fresh PDF for download
     const blob = await pdf(<TourInvoicePDF invoiceData={{ ...booking, pricing }} />).toBlob();

      // Optional: re-upload (harmless overwrite)
      const storageRef = ref(storage, `tour-pdfs/tour_${booking._id}.pdf`);
      await uploadBytes(storageRef, blob);

      // Trigger browser download
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `TourBookingInvoice_${booking._id || bookingId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      alert(" Invoice downloaded!"); 
    } catch (err) {
      console.error("Failed to generate PDF:", err);
      alert("Failed to generate invoice PDF. Please try again.");
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handlePayBalanceHdfc = async () => {
  try {
    const res = await axios.post(
      `${KeyUri.BACKEND_URI}/payments/hdfc/create-balance-order`,
      { type: "tour", bookingId: booking._id },
      { headers: { "Content-Type": "application/json" } }
    );
    if (!res.data?.payment_url) throw new Error("No payment URL returned");
    window.location.href = res.data.payment_url;
  } catch (err) {
    alert(err?.response?.data?.error || err.message || "Could not start balance payment.");
  }
};

  // Generate booking reference in format TGH-TBK-XXXX
  const getBookingRef = (bookingData) => {
    if (!bookingData) return "N/A";
    if (bookingData.bookingReference && bookingData.bookingReference.startsWith('TGH-TBK-')) {
      return bookingData.bookingReference;
    }
    // Extract last 4 digits from _id or generate random
    const idNum = bookingData._id ? bookingData._id.slice(-4).toUpperCase() : Math.floor(1000 + Math.random() * 9000);
    return `TGH-TBK-${idNum}`;
  };

  const handleCopy = () => {
    if (!booking?._id) return;
    const refToCopy = getBookingRef(booking);
    const textArea = document.createElement("textarea");
    textArea.value = refToCopy;
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand("copy");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Copy failed:", err);
    }
    document.body.removeChild(textArea);
  };

  // Add to Calendar
  const handleAddToCalendar = () => {
    if (!booking.date) {
      alert("Missing tour date");
      return;
    }

    const date = new Date(booking.date).toISOString().split("T")[0];
    const title = tour.title || "Your Tour";
    const locationStr = tour.location || "Meeting Point";
    const details = `Tour: ${tour.title}\nDate: ${travelDate}\nGuests: ${guestsDescription}\nBooking Ref: ${getBookingRef(booking)}`;

    const googleUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
      title
    )}&dates=${date}T09:00:00/${date}T18:00:00&details=${encodeURIComponent(
      details
    )}&location=${encodeURIComponent(locationStr)}`;

    window.open(googleUrl, "_blank");
  };


  const tour = booking?.tourId || {};
  const travelDate = booking?.date
    ? new Date(booking.date).toLocaleDateString("en-IN", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "N/A";

  const guestsDescription = `${booking?.guests || 1} Guest${
    (booking?.guests || 1) > 1 ? "s" : ""
  }`;

  const amount = booking?.amountPaid ?? booking?.amount ?? 0;

    const { baseAmount, gstAmount } = useMemo(() => {
  if (!booking || !pricing) return { baseAmount: amount, gstAmount: 0 };
  
  const totalPaid = Number(amount);
  const gstRate = Number(pricing?.data?.gst_price || 0);
  
  if (gstRate <= 0) return { baseAmount: totalPaid, gstAmount: 0 };
  
  const base = Math.round(totalPaid / (1 + gstRate / 100));
  const gst = totalPaid - base;
  
  return { baseAmount: base, gstAmount: gst };
}, [booking, pricing, amount]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 text-gray-700 text-sm">
        Loading your booking details...
      </div>
    );
  }

    if (paymentFailed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 text-gray-700 text-sm text-center px-4">
        Your payment was not completed. If money was deducted, it will be confirmed shortly. Otherwise please try booking again.
      </div>
    );
  }
  
    if (booking && ["pending", "failed"].includes(booking.status)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 text-gray-700 text-sm text-center px-4">
        Payment for this booking has not been completed.
      </div>
    );
  }
  
  if (!booking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 text-gray-700 text-sm">
        No booking details found. Please check your booking ID.
      </div>
    );
  }

  // Use the booking reference helper
  const bookingRef = getBookingRef(booking);

  const paymentRef =
  booking.razorpayPaymentId ||
  booking.cashfreePaymentId ||
  [...(booking.paymentHistory || [])]
    .reverse()
    .find((p) => p.status === "success" && p.paymentId)?.paymentId ||
  "N/A";

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-900 pb-12 mt-32">
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center p-3 bg-green-100 rounded-full mb-4 ring-8 ring-green-50 relative">
            <Check
              size={40}
              className="text-green-600 animate-bounce"
              strokeWidth={3}
            />
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold mb-2">
            Tour Booking Confirmed!
          </h1>
          <p className="text-lg text-gray-600 max-w-xl mx-auto">
            Get ready for{" "}
            <span className="font-semibold text-gray-900">
              {tour.title || "your adventure"}
            </span>
            . A confirmation email has been sent to{" "}
            <span className="font-semibold">{booking.email}</span>.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left: main card */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Header image */}
              <div className="relative h-48 sm:h-64 bg-gray-200">
                {tour.image_url ? (
                  <img
                    src={tour.image_url}
                    alt={tour.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-gray-200 via-blue-200 to-purple-300 flex items-center justify-center">
                    <span className="text-5xl opacity-20">🏔️</span>
                  </div>
                )}
              </div>

              {/* Body */}
              <div className="p-6">
                {/* Title + ID */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                  <div>
                    <h2 className="text-2xl font-bold leading-tight">
                      {tour.title || "Tour"}
                    </h2>
                    <div className="flex items-center text-gray-500 mt-1 text-sm font-medium">
                      <MapPin size={16} className="mr-1.5 text-indigo-500" />
                      <span>{tour.location || "Destination"}</span>
                    </div>
                  </div>

                  <div className="w-full sm:w-auto bg-gray-50 border border-gray-100 rounded-lg p-3 flex flex-col items-center sm:items-end">
                    <span className="text-xs uppercase tracking-wider text-gray-400 font-bold mb-1">
                      Reference ID
                    </span>
                    <button
                      onClick={handleCopy}
                      className="group flex items-center gap-2 text-indigo-700 font-mono text-lg font-bold hover:text-indigo-900 transition-colors"
                    >
                      {bookingRef}
                      {copied ? (
                        <Check size={18} className="text-green-500" />
                      ) : (
                        <Copy
                          size={18}
                          className="text-indigo-300 group-hover:text-indigo-600"
                        />
                      )}
                    </button>
                    {copied && (
                      <span className="text-xs text-green-600 font-medium mt-1">
                        Copied!
                      </span>
                    )}
                  </div>
                </div>

                {/* Timeline: date only */}
                <div className="flex flex-col sm:flex-row gap-6 mb-8 relative">
                  <div className="flex-1 flex flex-row sm:flex-col items-center gap-4 sm:gap-2">
                    <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 border border-indigo-100 shrink-0">
                      <Calendar size={20} />
                    </div>
                    <div className="text-left sm:text-center">
                      <p className="text-xs text-gray-400 font-bold uppercase tracking-wide">
                        Travel Date
                      </p>
                      <p className="text-lg font-bold">{travelDate}</p>
                    </div>
                  </div>
                </div>

                {/* Guests & status */}
                <div className="bg-gray-50 rounded-xl p-4 sm:p-5 flex flex-col sm:flex-row gap-6 sm:items-center justify-between border border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm text-gray-600">
                      <Users size={20} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold">Guests</p>
                      <p className="text-sm text-gray-600">{guestsDescription}</p>
                    </div>
                  </div>
                  <div className="h-px w-full sm:h-8 sm:w-px bg-gray-200" />
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm text-gray-600">
                      <ShieldCheck size={20} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold">Status</p>
                      <span className="inline-flex px-3 py-1 bg-green-100 text-green-800 text-xs font-bold rounded-full border border-green-200">
                        {booking.status?.toUpperCase() || "CONFIRMED"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>


<div className="bg-gray-50/50 p-4 border-t border-gray-100">

<PhoneAction 
  phone="+919883587965"  
  label="Need Help?"
  size="medium"
/>

</div>

            </div>
          </div>

          {/* Right: payment & actions */}
          <div className="space-y-6">
            {/* Quick actions */}
            <div className="grid grid-cols-2 gap-3">
              <button 
                onClick={handleAddToCalendar}
                className="p-4 bg-white border border-gray-200 rounded-xl hover:border-indigo-300 hover:shadow-md transition-all group text-left"
              >
                <Calendar className="h-6 w-6 text-indigo-600 mb-3 group-hover:scale-110 transition-transform origin-left" />
                <span className="block text-sm font-bold">Add to Calendar</span>
                <span className="text-xs text-gray-500">Google, Apple</span>
              </button>
              <button
                onClick={handleDownloadPDF}
                disabled={isGeneratingPdf}
                className="p-4 bg-white border border-gray-200 rounded-xl hover:border-indigo-300 hover:shadow-md transition-all group text-left disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Download className="h-6 w-6 text-indigo-600 mb-3 group-hover:scale-110 transition-transform origin-left" />
                <span className="block text-sm font-bold">Download Invoice</span>
                <span className="text-xs text-gray-500">
                  {isGeneratingPdf ? "Generating..." : "Receipt"}
                </span>
              </button>
            </div>

            {/* Payment summary */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                <h3 className="font-bold">Payment Details</h3>
               <span className="px-2.5 py-1 rounded-md text-xs font-bold bg-green-100 text-green-700 border border-green-200">
  {booking.paymentStatus === "partially_paid" ? "PARTIALLY PAID" : "PAID"}
</span>
              </div>

              <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                  isDetailsOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                }`}
              >
                <div className="p-5 space-y-3">
                  <div className="flex justify-between text-sm text-gray-600">
    <span>Base Price</span>
    <span className="font-medium text-gray-900">
      ₹{baseAmount?.toLocaleString("en-IN") || amount.toLocaleString("en-IN")}
    </span>
  </div>
  
  <div className="flex justify-between text-sm text-gray-600">
    <span>GST ({pricing?.data?.gst_price || 0}%)</span>
    <span className="font-medium text-gray-900">
      ₹{gstAmount?.toLocaleString("en-IN") || "0"}
    </span>
  </div>
  
  <div className="pt-4 border-t border-dashed border-gray-200 flex justify-between items-center">
    <span className="font-bold text-gray-900">Total Paid</span>
    <span className="font-bold text-xl text-gray-900">
      ₹{amount.toLocaleString("en-IN")} INR
    </span>
  </div>

{booking.paymentStatus === "partially_paid" && booking.balanceAmount > 0 && (
  <div className="flex justify-between items-center text-orange-700">
    <span className="font-semibold">Balance Due</span>
    <span className="font-bold">₹{Number(booking.balanceAmount).toLocaleString("en-IN")}</span>
  </div>
)}

{booking.paymentGateway === "hdfc" &&
  booking.paymentStatus === "partially_paid" &&
  booking.balanceAmount > 0 && (
    <button
      onClick={handlePayBalanceHdfc}
      className="w-full py-3 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm shadow-md transition-colors"
    >
      Pay Balance ₹{Number(booking.balanceAmount).toLocaleString("en-IN")} via HDFC
    </button>
  )}
                  <div className="text-xs text-gray-500 bg-gray-100 p-2 rounded-md font-mono break-all">
                    Payment ID: {paymentRef}
                  </div>
                </div>
              </div>

              <button
                onClick={() => setIsDetailsOpen(!isDetailsOpen)}
                className="w-full py-2 bg-gray-50 hover:bg-gray-100 text-xs text-gray-500 font-medium flex items-center justify-center gap-1 transition-colors border-t border-gray-100"
              >
                {isDetailsOpen ? "Hide Details" : "Show Details"}
                {isDetailsOpen ? (
                  <ChevronUp size={14} />
                ) : (
                  <ChevronDown size={14} />
                )}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
